import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { CalendarDays, User, Tag, ArrowLeft } from "lucide-react";
import { blogPosts } from "../data/blogData";
import { trackAndNavigate } from "../utils/navigate";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const BlogArticle = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = post.seoTitle;
      // SEO meta description
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = post.seoDescription;
    }
    return () => {
      document.title = "Integro Clinics | Medical Cannabis Clinic UK";
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const relatedPosts = blogPosts.filter((p) =>
    post.relatedSlugs?.includes(p.slug)
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Navbar />

      {/* Hero image */}
      <div className="relative h-72 md:h-[420px] overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#012d5c]/80 via-[#012d5c]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-10 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
            {post.category}
          </span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-4xl mx-auto">
            {post.title}
          </h1>
        </div>
      </div>

      {/* Article meta + content */}
      <div className="container mx-auto px-4 py-10">
        <div className="max-w-3xl mx-auto">

          {/* Back link + meta row */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-gray-200">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#005eb8] hover:text-[#4caf50] text-sm font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to News & Education
            </Link>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4 text-[#4caf50]" />
                {post.date}
              </span>
              {post.author && (
                <span className="flex items-center gap-1.5">
                  <User className="w-4 h-4 text-[#4caf50]" />
                  {post.author}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Tag className="w-4 h-4 text-[#4caf50]" />
                {post.category}
              </span>
            </div>
          </div>

          {/* Article body */}
          <article className="prose-integro">
            <post.Body />
          </article>

          {/* Sources & References */}
          {post.sources && post.sources.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h2 className="text-lg font-bold text-[#012d5c] mb-4">Sources &amp; References</h2>
              <ol className="space-y-2">
                {post.sources.map((source, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="text-[#4caf50] font-bold shrink-0">{i + 1}.</span>
                    <span>
                      {source.title}.{" "}
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#005eb8] hover:text-[#4caf50] underline underline-offset-2 transition-colors break-all"
                      >
                        {source.url}
                      </a>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Eligibility CTA */}
          <div className="mt-16 bg-[#012d5c] rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Could You Be Eligible for Treatment?
            </h2>
            <p className="text-blue-200 mb-8 max-w-xl mx-auto">
              Complete our eligibility check to find out whether a specialist
              clinical assessment may be appropriate for your condition.
            </p>
            <a
              href="https://app.integroclinics.com/v2/registration"
              onClick={(e) => { e.preventDefault(); trackAndNavigate("https://app.integroclinics.com/v2/registration", 'eligibility_start', { entry_point: 'website' }); }}
              className="inline-block bg-[#4caf50] hover:bg-[#3d8b40] text-white font-bold px-10 py-4 rounded-lg text-lg transition-colors shadow-lg"
            >
              Check your eligibility
            </a>
          </div>
        </div>

        {/* Related posts */}
        {relatedPosts.length > 0 && (
          <div className="max-w-5xl mx-auto mt-20">
            <h2 className="text-2xl font-bold text-[#012d5c] mb-8 flex items-center gap-3">
              <span className="w-2 h-8 bg-[#4caf50] rounded-full" />
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group border border-gray-100"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={related.image}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-[#4caf50] text-xs font-bold uppercase tracking-wider">
                      {related.category}
                    </span>
                    <h3 className="text-base font-bold text-[#012d5c] mt-2 leading-snug group-hover:text-[#005eb8] transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <p className="text-gray-500 text-sm mt-2 line-clamp-2 leading-relaxed">
                      {related.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default BlogArticle;
